import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  buildStatusMeta,
  FALLBACK_STATUS_ROWS,
  type StatusMetaMap,
} from "@/lib/task-meta";
import type { Database } from "@shared/database.types";

export type TaskStatusRow = Database["public"]["Tables"]["task_statuses"]["Row"];

/**
 * Genera la key stabile di uno stato dalla label: lowercase, underscore,
 * unica rispetto alle key esistenti (anche archiviate — l'unicità DB le include).
 * La key non si rinomina mai: si rinomina solo la label.
 */
export function statusKeyFromLabel(
  label: string,
  existingKeys: Iterable<string>,
): string {
  const base =
    label
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "stato";
  const keys = new Set(existingKeys);
  if (!keys.has(base)) return base;
  let i = 2;
  while (keys.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}

/**
 * Stati pipeline dell'agenzia, sort_order asc. Una sola query in cache
 * (tutte le righe): il flag includeArchived filtra client-side via select.
 */
export function useTaskStatuses(opts: { includeArchived?: boolean } = {}) {
  const { agencyId } = useAuth();
  const includeArchived = opts.includeArchived ?? false;

  return useQuery({
    queryKey: ["task-statuses", agencyId],
    enabled: !!agencyId,
    queryFn: async (): Promise<TaskStatusRow[]> => {
      const { data, error } = await supabase
        .from("task_statuses")
        .select("*")
        .eq("agency_id", agencyId!)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as TaskStatusRow[];
    },
    select: includeArchived
      ? undefined
      : (rows: TaskStatusRow[]) => rows.filter((r) => !r.archived),
  });
}

/**
 * Meta pipeline pronta per la UI (label, colori, defaultKey, doneKeys).
 * Finché le righe non sono caricate degrada sul seed statico, così il
 * kanban non parte mai vuoto.
 */
export function useStatusMeta(): StatusMetaMap {
  const { data } = useTaskStatuses({ includeArchived: true });
  return useMemo(
    () => buildStatusMeta(data && data.length > 0 ? data : FALLBACK_STATUS_ROWS),
    [data],
  );
}

export interface CreateStatusInput {
  label: string;
  color: string;
  is_done?: boolean;
}

export function useCreateStatus() {
  const qc = useQueryClient();
  const { agencyId } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateStatusInput) => {
      if (!agencyId) throw new Error("agency_id mancante");
      // Key e sort_order derivati dallo stato attuale sul server:
      // l'unicità della key vale anche contro gli stati archiviati.
      const { data: existing, error: exErr } = await supabase
        .from("task_statuses")
        .select("key, sort_order")
        .eq("agency_id", agencyId);
      if (exErr) throw exErr;
      const rows = (existing ?? []) as Pick<TaskStatusRow, "key" | "sort_order">[];
      const key = statusKeyFromLabel(input.label, rows.map((r) => r.key));
      const sort_order = Math.max(0, ...rows.map((r) => r.sort_order)) + 10;

      const { data, error } = await supabase
        .from("task_statuses")
        .insert({
          agency_id: agencyId,
          key,
          label: input.label.trim(),
          color: input.color,
          sort_order,
          is_done: input.is_done ?? false,
        })
        .select()
        .single();
      if (error) throw error;
      return data as TaskStatusRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task-statuses"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export interface StatusPatch {
  label?: string;
  color?: string;
  sort_order?: number;
  is_done?: boolean;
  is_default?: boolean;
  archived?: boolean;
}

export function useUpdateStatus() {
  const qc = useQueryClient();
  const { agencyId } = useAuth();

  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: StatusPatch }) => {
      // is_default è esclusivo: prima togli il flag agli altri, poi settalo qui
      if (patch.is_default === true) {
        if (!agencyId) throw new Error("agency_id mancante");
        const { error: clearErr } = await supabase
          .from("task_statuses")
          .update({ is_default: false })
          .eq("agency_id", agencyId)
          .neq("id", id);
        if (clearErr) throw clearErr;
      }
      const { data, error } = await supabase
        .from("task_statuses")
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as TaskStatusRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task-statuses"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export interface ArchiveStatusInput {
  id: string;
  /** Key dello stato da archiviare (per la riassegnazione delle task) */
  key: string;
  /** Stato di destinazione delle task rimaste su questo stato */
  reassignTo?: string | null;
}

export function useArchiveStatus() {
  const qc = useQueryClient();
  const { agencyId } = useAuth();

  return useMutation({
    mutationFn: async ({ id, key, reassignTo }: ArchiveStatusInput) => {
      if (!agencyId) throw new Error("agency_id mancante");
      // Riassegna PRIMA di archiviare: nessuna task resta su uno stato invisibile
      if (reassignTo && reassignTo !== key) {
        const { error: moveErr } = await supabase
          .from("tasks")
          .update({ status: reassignTo })
          .eq("agency_id", agencyId)
          .eq("status", key);
        if (moveErr) throw moveErr;
      }
      const { error } = await supabase
        .from("task_statuses")
        .update({ archived: true, is_default: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task-statuses"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}
